// ---------------------------------------------------------------------------
// Workspace chrome — what the top-left switcher may honestly claim right now.
//
// The switcher names WHOSE world you're in. Two sources disagree on a cold
// load: the URL knows immediately (/team/<slug>/… names a team), while the
// memberships fetch — which holds the team's display name — lands hundreds of
// milliseconds later. Resolving from memberships alone means every fresh team
// link renders "Personal" first, which is a wrong answer rather than a blank
// one. So: the URL decides WHICH workspace, memberships supply its NAME, and
// until both agree the chip says nothing.
// ---------------------------------------------------------------------------

import type { MembershipsInfo } from '@/lib/api'
import { parseWorkspaceRoute } from '@/lib/workspaceRoutes'

export type TeamOrg = MembershipsInfo['teamOrgs'][number]

/** Public plan-action pages under /team that carry no workspace slug. */
const TEAM_ACTION_PATHS = new Set(['/team/activate', '/team/cancel'])

/**
 * The team slug the URL is scoped to, or null when the surface is personal
 * (or not workspace-scoped at all).
 */
export function teamSlugFromPath(pathname: string | null | undefined): string | null {
  if (!pathname || TEAM_ACTION_PATHS.has(pathname)) return null
  const workspaceRoute = parseWorkspaceRoute(pathname)
  if (workspaceRoute?.destination.kind === 'personal') return null
  if (workspaceRoute?.destination.kind === 'team') return workspaceRoute.destination.slug
  return pathname.match(/^\/team\/([^/]+)/)?.[1] ?? null
}

export interface WorkspaceChipInput {
  pathname: string | null | undefined
  /** `useWorkspaceContext` — 'personal' or a team org id. */
  contextId: string
  /** Whether the persisted context has been applied yet. */
  hydrated: boolean
  /**
   * Workspace the user just picked whose route hasn't committed yet (ENG-723).
   * While it is set, the URL still names where you're LEAVING, so the pick wins.
   */
  pendingContextId: string | null
  memberships: MembershipsInfo | null
  membershipsLoading: boolean
}

export interface WorkspaceChip {
  /** The slug the URL scopes to, or null. */
  teamSlug: string | null
  /** The named team, once it can be resolved. Null means personal OR unknown. */
  activeTeam: TeamOrg | null
  /** True while the workspace cannot be named — render a placeholder, not "Personal". */
  resolving: boolean
}

/**
 * Resolve the workspace the chrome should present.
 *
 * On a team URL the slug wins outright, so switching workspaces repaints as
 * soon as the route changes. Elsewhere the persisted context id is the memory
 * of where you left off.
 *
 * The one exception is a switch still in flight: the picked workspace has been
 * adopted but its route hasn't committed, so the URL names the workspace being
 * LEFT. Deferring to the slug there would rename the chip back to the old team
 * for the length of the navigation — the same stale-name bug from the other
 * direction (ENG-723).
 */
export function resolveWorkspaceChip({
  pathname,
  contextId,
  hydrated,
  pendingContextId,
  memberships,
  membershipsLoading,
}: WorkspaceChipInput): WorkspaceChip {
  const workspaceRoute = pendingContextId === null ? parseWorkspaceRoute(pathname) : null
  const explicitPersonal = workspaceRoute?.destination.kind === 'personal'
  const teamSlug = pendingContextId === null ? teamSlugFromPath(pathname) : null
  const teams = memberships?.teamOrgs ?? []
  const activeTeam =
    (explicitPersonal
      ? null
      : teamSlug
        ? teams.find(team => team.slug === teamSlug)
        : teams.find(team => team.id === contextId)) ?? null

  // Personal is only safe to claim once nothing else can be true: no team in
  // the URL, no team remembered, and the memory has actually been read. A
  // failed fetch clears membershipsLoading with memberships still null — that
  // resolves to the old fallback rather than pinning a permanent placeholder.
  const couldBeATeam = explicitPersonal ? false : !hydrated || teamSlug !== null || contextId !== 'personal'
  const resolving = memberships === null && membershipsLoading && couldBeATeam

  return { teamSlug, activeTeam, resolving }
}
